import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterResources } from '@angular/router';
import {
  FORTRESS_CONFIG,
  FortressConfig,
  PermissionCheckService,
  UserPermissionCheckService,
} from '@psu-pennstateit-swe/apis/fortress';
import { provideAnalytics } from '@psu-pennstateit-swe/utils/analytics';
import {
  cacheInterceptor,
  LoadingEvents,
  provideRequestTracing,
  requestCacheInterceptor,
} from '@psu-pennstateit-swe/utils/browser';
import { provideCdnConfig } from '@psu-pennstateit-swe/utils/cdn';
import {
  AUTH_CONFIG_DD,
  AuthConfigWithDiscoveryDoc,
  authInterceptor,
  AuthService,
  OAuthErrorEvent,
  provideAuthConfigWithDiscoveryDoc,
  userIdRequestTracingInterceptor,
} from '@psu-pennstateit-swe/utils/security';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { filter, takeWhile } from 'rxjs';
import { APP_CONFIG, APP_PROPERTIES, AppConfig, SSRM_CONFIG, SsrmConfig } from './app-properties.model';
import { routes } from './app.routes';

const provideAppConfig = (appConfigFn: () => AppConfig): EnvironmentProviders =>
  makeEnvironmentProviders([
    {
      provide: APP_CONFIG,
      useFactory: appConfigFn,
    },
  ]);

const provideFortress = (fortressConfigFn: () => FortressConfig): EnvironmentProviders =>
  makeEnvironmentProviders([{ provide: FORTRESS_CONFIG, useFactory: fortressConfigFn }]);

const provideSsrm = (ssrmConfigFn: () => SsrmConfig): EnvironmentProviders =>
  makeEnvironmentProviders([{ provide: SSRM_CONFIG, useFactory: ssrmConfigFn }]);

export const initializeApp = async (
  authConfig: AuthConfigWithDiscoveryDoc,
  authService: AuthService,
  loadingEvents: LoadingEvents,
): Promise<void> => {
  authService.configure(authConfig);

  if (authConfig.showDebugInformation) {
    authService.enableDebug();
  }

  // Listen for error events until a token_received event is emitted
  authService.events
    .pipe(
      takeWhile((event) => event.type !== 'token_received'),
      filter((event): event is OAuthErrorEvent => 'reason' in event),
    )
    .subscribe((event) => {
      document.dispatchEvent(
        new CustomEvent('apperror', {
          bubbles: true,
          detail: event,
        }),
      );
    });

  await authService.loadDiscoveryDocument(authConfig.discoveryDocumentUrl);
  await authService.tryLogin();

  if (authService.isLoggedIn()) {
    authService.setupAutomaticSilentRefresh();
  }

  loadingEvents.triggerAppReady();
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnalytics(() => inject(APP_PROPERTIES).analytics),
    provideAppConfig(() => inject(APP_PROPERTIES).app),
    provideAppInitializer(() => initializeApp(inject(AUTH_CONFIG_DD), inject(AuthService), inject(LoadingEvents))),
    provideBrowserGlobalErrorListeners(),
    provideCdnConfig(() => inject(APP_PROPERTIES).cdn),
    provideFortress(() => inject(APP_PROPERTIES).fortress),
    provideHttpClient(
      withXhr(),
      withInterceptorsFromDi(),
      withInterceptors([authInterceptor, cacheInterceptor, requestCacheInterceptor, userIdRequestTracingInterceptor]),
    ),
    provideOAuthClient(),
    provideAuthConfigWithDiscoveryDoc(() => inject(APP_PROPERTIES).oauth),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterResources(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
    ),
    provideSsrm(() => inject(APP_PROPERTIES).ssrm),
    provideRequestTracing('student-success-role-management'),
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-symbols-outlined' } },
    { provide: PermissionCheckService, useClass: UserPermissionCheckService },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 4000, horizontalPosition: 'left', verticalPosition: 'bottom' },
    },
  ],
};
